import React, { useContext, useState } from "react";
import { FormProvider } from "react-hook-form";
import { CompanySearchModal } from "@components";
import { useAccountList } from "@hooks";
import { AuthContext, useAdminCompany } from "@context";
import { ROLES } from "@constants/roles";
import AccountListSection from "./AccountListSection";
import AccountSaveModal from "./AccountSaveModal";

const AccountPage = () => {
  const { auth, hasPermission } = useContext(AuthContext);
  const canWrite = hasPermission("USER_WRITE");
  const canDelete = hasPermission("USER_DELETE");
  const isAdmin = auth.role === ROLES.SUPER_ADMIN || auth.role === ROLES.ADMIN;

  const { adminCompCode, adminCompName, setAdminCompCode, setAdminCompName } = useAdminCompany();
  const compCode = isAdmin ? adminCompCode : auth?.compCode;
  const compName = isAdmin ? adminCompName : auth?.compName;

  const {
    methods,
    data,
    isLoading,
    isError,
    error,
    onSubmit,
    handleDelete,
    handleSelectAll,
    isAllChecked,
    handlePageChange,
    changePageSize,
    refetch,
    roleOptions: allRoleOptions,
    useYnOptions,
    handleSelectItem,
    selectedItems,
    pageIndex,
    recordCountPerPage,
  } = useAccountList(compCode, compName);

  const roleOptions = auth.role === ROLES.SUPER_ADMIN
    ? allRoleOptions
    : allRoleOptions.filter((opt) => opt.key !== "R0");

  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState("create"); // create | edit
  const [editingAccount, setEditingAccount] = useState(null);

  const handleCompanyModalSuccess = (selectedCompany) => {
    if (!selectedCompany) return;
    setCompanyModalOpen(false);
  };

  if (isLoading) return <div>로딩 중..</div>;
  if (isError) {
    console.log("isError:", error);
    return <div>오류가 발생했습니다: {error?.message || "알 수 없는 오류"}</div>;
  }

  const displayedCompanyName = isAdmin
    ? (adminCompCode ? adminCompName : adminCompName || "전체")
    : data?.company?.compName || compName;

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <div className="contents-wrap !mt-4 md:!mt-5 !px-3 sm:!px-4 md:!px-6 lg:!px-8 !pt-3 md:!pt-4 !pb-4 md:!pb-6 !text-[16px] md:!text-[17px] lg:!text-[18px]">
          <div className="page-title-wrap !mb-3 md:!mb-4 !flex-col sm:!flex-row !items-start sm:!items-center gap-2">
            <h2 className="!text-[26px] md:!text-[30px] !font-extrabold tracking-tight flex items-center gap-2">
              <span className="icon-dot-circle"></span>
              계정 관리
            </h2>
          </div>

          <div className="tab-cont-wrap">
            <div className="tab-cont-01 on">
              <div className="mt-4">
                <section>
                  <AccountListSection
                    data={data}
                    isAdmin={isAdmin}
                    companyName={displayedCompanyName}
                    onOpenCompanyModal={() => setCompanyModalOpen(true)}
                    onResetCompany={adminCompCode ? () => { setAdminCompCode(""); setAdminCompName(""); } : null}
                    canWrite={canWrite}
                    onOpenSaveModal={() => {
                      setFormMode("create");
                      setEditingAccount(null);
                      setFormModalOpen(true);
                    }}
                    onOpenEditModal={(item) => {
                      setFormMode("edit");
                      setEditingAccount(item);
                      setFormModalOpen(true);
                    }}
                    canDelete={canDelete}
                    onDelete={handleDelete}
                    selectedItems={selectedItems}
                    isAllChecked={isAllChecked}
                    handleSelectAll={handleSelectAll}
                    handleSelectItem={handleSelectItem}
                    handlePageChange={handlePageChange}
                    changePageSize={changePageSize}
                    pageIndex={pageIndex}
                    recordCountPerPage={recordCountPerPage}
                    roleOptions={roleOptions}
                  />
                </section>
              </div>
            </div>
          </div>
        </div>
      </form>

      {isAdmin && (
        <CompanySearchModal
          isOpen={companyModalOpen}
          onClose={() => setCompanyModalOpen(false)}
          onConfirm={handleCompanyModalSuccess}
          showAllOption
        />
      )}

      <AccountSaveModal
        show={formModalOpen}
        mode={formMode}
        account={editingAccount}
        onClose={() => setFormModalOpen(false)}
        onSaved={() => refetch()}
        compCode={compCode}
        compName={displayedCompanyName}
        roleOptions={roleOptions}
        useYnOptions={useYnOptions}
      />
    </FormProvider>
  );
};

export default AccountPage;
